import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import IconsBar from "../../components/IconsBar/IconsBar";
import SelectSys from "../../components/Systech/SelectSys/SelectSys";
import styled from "styled-components";
import { getGradeChangeLogs, getCaptions } from "../../api/autenticoAPI";
import { getAllClassrooms, getSubjects, getStudentsByClassroom } from "../../api/sponteAPI";
import { normalizeSubjects, normalizeStudentsByClassroom } from "../../utils/sponteDataNormalizer";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #1e958c 0%, #0b4f6c 40%, #f5f7fb 100%);
`;

const ContentArea = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 16px auto 24px;
  padding: 20px 16px 28px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
  display: flex;
  flex-direction: column;
  gap: 18px;
  backdrop-filter: blur(8px);
`;

const FiltersArea = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #f5f7fb;
  border-radius: 12px;
`;

const FiltersRow = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  align-items: end;
  width: 100%;
  
  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (min-width: 900px) and (max-width: 1199px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FiltersRowSecondary = styled(Box)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 16px;
  align-items: end;
  width: 100%;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  max-height: 600px;
  border-radius: 12px;
`;

const PageTitle = styled.span`
  display: block;
  text-align: center;
  font-weight: 700;
  font-size: 22px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1e958c;
  margin: 4px auto 8px;
  width: 100%;
`;

const StyledTableHead = styled(TableHead)`
  background: #e3f2fd !important;
  
  th {
    color: #1a1a1a !important;
    font-weight: 600 !important;
  }
`;

const GradeLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Listas para os seletores
  const [turmas, setTurmas] = useState([]);
  const [disciplinas, setDisciplinas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [, setCaptions] = useState(null);
  const [loadingTurmas, setLoadingTurmas] = useState(false);
  const [loadingDisciplinas, setLoadingDisciplinas] = useState(false);
  
  // Filtros selecionados
  const [turmaSelecionada, setTurmaSelecionada] = useState("");
  const [periodoSelecionado, setPeriodoSelecionado] = useState("");
  const [disciplinaSelecionada, setDisciplinaSelecionada] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [usuarioSelecionado, setUsuarioSelecionado] = useState("");
  
  // Lista de usuários únicos que fizeram alterações
  const [usuariosDisponiveis, setUsuariosDisponiveis] = useState([]);
  
  // Ano letivo - inicializar com o ano atual (mínimo 2025, máximo 2026)
  const currentYear = new Date().getFullYear();
  const [anoLetivo, setAnoLetivo] = useState(
    currentYear >= 2026 ? 2026 : currentYear >= 2025 ? currentYear : 2025
  );

  // Carregar turmas quando o ano letivo mudar
  useEffect(() => {
    const carregarTurmas = async () => {
      setLoadingTurmas(true);
      // Limpar seleções ao mudar o ano letivo
      setTurmaSelecionada("");
      setDisciplinaSelecionada("");
      setPeriodoSelecionado("");
      setLogs([]);
      setDisciplinas([]);
      setAlunos([]);
      
      try {
        const response = await getAllClassrooms(String(anoLetivo));
        const turmasFormatadas = response.map((turma) => ({
          nome: turma.Nome,
          id: turma.TurmaID,
        }));
        setTurmas(turmasFormatadas);
      } catch (err) {
        console.error("Erro ao carregar turmas:", err);
        setError("Erro ao carregar turmas. Verifique se o ano letivo está correto.");
      } finally {
        setLoadingTurmas(false);
      }
    };
    carregarTurmas();
  }, [anoLetivo]);

  // Carregar disciplinas quando uma turma for selecionada
  useEffect(() => {
    const carregarDisciplinas = async () => {
      if (!turmaSelecionada) {
        setDisciplinas([]);
        setDisciplinaSelecionada("");
        return;
      }

      setLoadingDisciplinas(true);
      try {
        const turmaId = turmas.find((t) => t.nome === turmaSelecionada)?.id;
        if (!turmaId) return;

        const response = await getSubjects(turmaId);
        const disciplinasNormalizadas = normalizeSubjects(response);
        const disciplinasFormatadas = disciplinasNormalizadas.map((disc) => ({
          id: disc.DisciplinaID,
          nome: disc.Nome,
        }));
        setDisciplinas(disciplinasFormatadas);
        setDisciplinaSelecionada(""); // Reset disciplina ao mudar turma
      } catch (err) {
        console.error("Erro ao carregar disciplinas:", err);
        setError("Erro ao carregar disciplinas");
      } finally {
        setLoadingDisciplinas(false);
      }
    };
    carregarDisciplinas();
  }, [turmaSelecionada, turmas]);

  // Carregar alunos e captions quando turma e período forem selecionados
  useEffect(() => {
    const carregarDadosAdicionais = async () => {
      if (!turmaSelecionada || !periodoSelecionado) {
        setAlunos([]);
        setCaptions(null);
        return;
      }

      try {
        const turmaId = turmas.find((t) => t.nome === turmaSelecionada)?.id;
        if (!turmaId) return;

        // Buscar alunos da turma
        const alunosResponse = await getStudentsByClassroom(turmaId);
        const alunosNormalizados = normalizeStudentsByClassroom(alunosResponse);
        setAlunos(alunosNormalizados);

        // Buscar captions (nomes das atividades)
        try {
          const captionsResponse = await getCaptions(turmaId, periodoSelecionado);
          setCaptions(captionsResponse);
        } catch (err) {

          setCaptions(null);
        }
      } catch (err) {
        console.error("Erro ao carregar dados adicionais:", err);
      }
    };

    carregarDadosAdicionais();
  }, [turmaSelecionada, periodoSelecionado, turmas]);

  // Limpar filtros opcionais quando os filtros principais mudarem
  useEffect(() => {
    if (!turmaSelecionada || !periodoSelecionado || !disciplinaSelecionada) {
      setDataInicio("");
      setDataFim("");
      setUsuarioSelecionado("");
      setUsuariosDisponiveis([]);
    }
  }, [turmaSelecionada, periodoSelecionado, disciplinaSelecionada]);

  // Buscar logs quando os filtros principais mudarem
  useEffect(() => {
    const buscarLogs = async () => {
      if (!turmaSelecionada || !periodoSelecionado || !disciplinaSelecionada) {
        setLogs([]);
        setUsuariosDisponiveis([]);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const turmaId = turmas.find((t) => t.nome === turmaSelecionada)?.id;
        const disciplinaId = disciplinas.find((d) => d.nome === disciplinaSelecionada)?.id;

        if (!turmaId || !disciplinaId) {
          setLogs([]);
          return;
        }

        const filters = {
          TurmaID: turmaId,
          DisciplinaID: disciplinaId,
          period: periodoSelecionado,
        };

        // Adicionar filtros opcionais de data
        if (dataInicio) {
          filters.data_inicio = dataInicio;
        }
        if (dataFim) {
          filters.data_fim = dataFim;
        }
        // Adicionar filtro opcional de usuário
        if (usuarioSelecionado) {
          filters.usuario_id = usuarioSelecionado;
        }

        const response = await getGradeChangeLogs(filters);
        const logsRecebidos = response.logs || [];
        setLogs(logsRecebidos);
        
        // Extrair lista de usuários únicos dos logs
        const usuariosUnicos = [];
        const usuariosMap = new Map();
        logsRecebidos.forEach(log => {
          if (log.usuario && log.usuario.id && !usuariosMap.has(log.usuario.id)) {
            usuariosMap.set(log.usuario.id, log.usuario);
            usuariosUnicos.push({
              id: log.usuario.id,
              nome: log.usuario.nome,
              level: log.usuario.level,
            });
          }
        });
        // Ordenar usuários por nome
        usuariosUnicos.sort((a, b) => a.nome.localeCompare(b.nome));
        setUsuariosDisponiveis(usuariosUnicos);
        
        // Se o usuário selecionado não estiver mais na lista, limpar a seleção
        if (usuarioSelecionado && !usuariosUnicos.find(u => u.id.toString() === usuarioSelecionado.toString())) {
          setUsuarioSelecionado("");
        }
        
        if (logsRecebidos.length === 0) {
          setError("Nenhuma alteração encontrada para os filtros selecionados");
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Erro ao buscar logs");
        console.error("Erro ao buscar logs:", err);
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };

    buscarLogs();
  }, [turmaSelecionada, periodoSelecionado, disciplinaSelecionada, dataInicio, dataFim, usuarioSelecionado, turmas, disciplinas]);


  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const getLevelLabel = (level) => {
    const labels = {
      coordinator: "Coordenador",
      director: "Diretor",
      teacher: "Professor",
      assistant: "Auxiliar",
      secretary: "Secretário",
      admin: "Administrador",
    };
    return labels[level] || level;
  };

  const getCampoLabel = (campo) => {
    // O campo já vem com o nome da atividade do banco (salvo do grade_captions)
    // Então apenas retornamos o campo diretamente
    // Se for uma sigla conhecida sem caption, formatamos
    const campoLower = campo?.toLowerCase();
    
    // Campos que não têm caption (mantém sigla)
    const siglasPadrao = {
      rb: "RB",
      ef: "EF",
      ac1: "AC1",
      ac2: "AC2",
      mb: "MB",
      rm: "RM",
      media_final: "Média Anual (MA)",
      exame_final: "Exame Final",
      situacao: "Situação",
      visibilidade: "Visibilidade",
    };
    
    if (siglasPadrao[campoLower]) {
      return siglasPadrao[campoLower];
    }
    
    // Para outros campos, retorna o valor salvo (que já é o nome da atividade)
    return campo || "Desconhecido";
  };

  const getNomeAluno = (log) => {
    // Primeiro tenta usar o nome salvo no log (prioridade máxima)
    if (log.aluno_nome && log.aluno_nome.trim() !== '') {
      return log.aluno_nome;
    }
    // Se não tiver no log, busca na lista de alunos carregados
    if (alunos && alunos.length > 0) {
      const aluno = alunos.find((a) => {
        const alunoId = a.AlunoID || a.AlunoId;
        return alunoId === log.AlunoID;
      });
      if (aluno && aluno.Nome) {
        return aluno.Nome;
      }
    }
    // Fallback: retorna apenas o ID se não encontrar nome
    return `Aluno ${log.AlunoID}`;
  };

  // Agrupar logs por aluno para melhor visualização
  const logsAgrupados = logs.reduce((acc, log) => {
    const key = `${log.AlunoID}`;
    if (!acc[key]) {
      acc[key] = {
        AlunoID: log.AlunoID,
        alteracoes: [],
      };
    }
    acc[key].alteracoes.push(log);
    return acc;
  }, {});


  return (
    <Container>
      <Header />
      <IconsBar />
      <ContentArea>
        <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
          <SelectSys anoLetivo={anoLetivo} setAnoLetivo={setAnoLetivo} />
          <PageTitle>Log de Alterações de Notas</PageTitle>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Selecione a turma, período e disciplina para ver quem alterou as notas e quais foram as alterações.
        </Typography>

        <FiltersArea>
          {/* Primeira linha: Turma, Período e Disciplina */}
          <FiltersRow>
            <FormControl 
              fullWidth 
              size="small"
              sx={{
                '& .MuiFormHelperText-root': {
                  display: 'none',
                },
              }}
            >
              <InputLabel id="turma-label">Turma</InputLabel>
              <Select
                labelId="turma-label"
                value={turmaSelecionada}
                onChange={(e) => setTurmaSelecionada(e.target.value)}
                label="Turma"
                disabled={loadingTurmas}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {turmas.map((turma) => (
                  <MenuItem key={turma.id} value={turma.nome}>
                    {turma.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              fullWidth 
              size="small"
              sx={{
                '& .MuiFormHelperText-root': {
                  display: 'none',
                },
              }}
            >
              <InputLabel id="periodo-label">Período</InputLabel>
              <Select
                labelId="periodo-label"
                value={periodoSelecionado}
                onChange={(e) => setPeriodoSelecionado(e.target.value)}
                label="Período"
              >
                <MenuItem value="1">1º Bimestre</MenuItem>
                <MenuItem value="2">2º Bimestre</MenuItem>
                <MenuItem value="3">3º Bimestre</MenuItem>
                <MenuItem value="4">4º Bimestre</MenuItem>
              </Select>
            </FormControl>

            <FormControl 
              fullWidth 
              size="small" 
              disabled={!turmaSelecionada || loadingDisciplinas}
              sx={{
                '& .MuiFormHelperText-root': {
                  display: 'none',
                },
              }}
            >
              <InputLabel id="disciplina-label">Disciplina</InputLabel>
              <Select
                labelId="disciplina-label"
                value={disciplinaSelecionada}
                onChange={(e) => setDisciplinaSelecionada(e.target.value)}
                label="Disciplina"
              >
                {disciplinas.map((disc) => (
                  <MenuItem key={disc.id} value={disc.nome}>
                    {disc.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FiltersRow>

          {/* Segunda linha: Usuário, Data Início, Data Fim e Botão */}
          <FiltersRowSecondary>
            <FormControl 
              fullWidth 
              size="small" 
              disabled={!turmaSelecionada || !periodoSelecionado || !disciplinaSelecionada || usuariosDisponiveis.length === 0}
              sx={{
                '& .MuiFormHelperText-root': {
                  display: 'none',
                },
                '& .MuiInputBase-input': {
                  '&::placeholder': {
                    opacity: 0,
                  },
                },
              }}
            >
              <InputLabel id="usuario-label">Usuário</InputLabel>
              <Select
                labelId="usuario-label"
                value={usuarioSelecionado}
                onChange={(e) => setUsuarioSelecionado(e.target.value)}
                label="Usuário"
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected === '' || selected === null || selected === undefined) {
                    return '';
                  }
                  const usuario = usuariosDisponiveis.find(u => u.id.toString() === selected.toString());
                  return usuario ? `${usuario.nome} (${getLevelLabel(usuario.level)})` : '';
                }}
                sx={{
                  '& .MuiSelect-select': {
                    '&:empty::before': {
                      content: '""',
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>Todos os usuários</em>
                </MenuItem>
                {usuariosDisponiveis.map((usuario) => (
                  <MenuItem key={usuario.id} value={usuario.id}>
                    {usuario.nome} ({getLevelLabel(usuario.level)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Data Início"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              slotProps={{
                htmlInput: {
                  placeholder: '',
                },
              }}
              sx={{
                '& .MuiFormHelperText-root': {
                  display: 'none',
                },
              }}
              disabled={!turmaSelecionada || !periodoSelecionado || !disciplinaSelecionada}
            />

            <TextField
              fullWidth
              label="Data Fim"
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              slotProps={{
                htmlInput: {
                  placeholder: '',
                },
              }}
              sx={{
                '& .MuiFormHelperText-root': {
                  display: 'none',
                },
              }}
              disabled={!turmaSelecionada || !periodoSelecionado || !disciplinaSelecionada}
            />

            {(dataInicio || dataFim || usuarioSelecionado) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setDataInicio("");
                  setDataFim("");
                  setUsuarioSelecionado("");
                }}
                sx={{ height: '40px', whiteSpace: 'nowrap' }}
              >
                Limpar
              </Button>
            )}
          </FiltersRowSecondary>
        </FiltersArea>

        {error && (
          <Alert severity={logs.length === 0 ? "info" : "error"} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {!loading && logs.length > 0 && (
          <StyledTableContainer component={Paper}>
            <Table stickyHeader>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Aluno</TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Atividade</TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Valor Anterior</TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Valor Novo</TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Alterado Por</TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Data/Hora</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {Object.values(logsAgrupados)
                  .flatMap((grupo) => grupo.alteracoes)
                  .map((log, index) => (
                    <TableRow key={`${log.id}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {getNomeAluno(log)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getCampoLabel(log.campo_alterado)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          title={log.campo_alterado}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: log.valor_anterior ? "text.primary" : "text.secondary",
                            fontStyle: log.valor_anterior ? "normal" : "italic",
                          }}
                        >
                          {log.valor_anterior || "(vazio)"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{
                            color: "success.main",
                          }}
                        >
                          {log.valor_novo || "(vazio)"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {log.usuario.nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getLevelLabel(log.usuario.level)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(log.data_alteracao)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}

        {!loading && logs.length === 0 && turmaSelecionada && periodoSelecionado && disciplinaSelecionada && (
          <Box p={3} textAlign="center">
            <Typography variant="body1" color="text.secondary">
              Nenhuma alteração encontrada para os filtros selecionados.
            </Typography>
          </Box>
        )}
      </ContentArea>
    </Container>
  );
};

export default GradeLogs;
